"use client"
import css from "@css/modules/HottestDeals.module.scss"
import compCss from "@css/Components.module.scss"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { useGeneralData } from "@components/GeneralData/GeneralData"
import { useDealsContext } from "./context"
import { DealSelectFilter } from "./DealSelectFilter"
import { useUser } from "@lib/api/user"
import { Toggle } from "@components/Toggle"
import Nouislider from "nouislider-react"
import "nouislider/dist/nouislider.css"
import cn from "classnames"
import { DealsNavigation } from "./DealsNavigation"
import { Dropdown, MenuProps } from "antd"
import { Sort } from "@css/icons/SVG"
import { getMonthNum } from "@utils/index"

const range = {
    min: 20,
    "80%": [300, 5],
    "90%": [600, 5],
    max: 1600
}

interface Formatter {
    to(val: number): string | number
    from(val: string | number): number
}

export function DealsFilters({ use_month }: { use_month: string }) {
    const { setState, resorts, useMonth, withSold, pointsMin, pointsMax } = useDealsContext()
    const startPoint = useRef([pointsMin, pointsMax])
    const minRef = useRef<HTMLDivElement>(null)
    const maxRef = useRef<HTMLDivElement>(null)
    const { resorts: pResorts } = useGeneralData()
    const { isAuthenticated } = useUser()
    const onRangeChange = useCallback(
        (tooltip, nup, [min, max]) => {
            const pointsMax = Math.round(max)
            const pointsMin = Math.round(min)
            setState({ pointsMin, pointsMax })
        },
        [setState]
    )

    const tooltips = useMemo<Formatter[]>(() => {
        return [
            {
                to(val: number) {
                    minRef.current.innerHTML = val > 1500 ? "Max" : Math.round(val).toString()
                    return val.toString()
                },
                from(val: string | number): number {
                    return Number(val)
                }
            },
            {
                to(val: number) {
                    maxRef.current.innerHTML = val > 1500 ? "Max" : Math.round(val).toString()
                    return val.toString()
                },
                from(val: string | number): number {
                    return Number(val)
                }
            }
        ]
    }, [])
    const sliderRef = useRef(null)
    useEffect(() => {
        if (sliderRef.current?.noUiSlider) {
            sliderRef.current?.noUiSlider.set([pointsMin, pointsMax], true, true)
        }
    }, [pointsMin, pointsMax])
    return (
        <div className={css.filters}>
            <DealsNavigation />
            <DealSelectFilter
                value={resorts}
                disabled={!isAuthenticated}
                onChange={useCallback((e: string[]) => setState({ resorts: e?.map((i) => Number(i)) }), [setState])}
                className={css.select}
                placeholder="Resort"
                options={useMemo(
                    () =>
                        pResorts?.map((i) => {
                            return { value: i.id, label: i.short_name }
                        }),
                    [pResorts, resorts]
                )}
            />
            <DealSelectFilter
                value={useMonth}
                disabled={!isAuthenticated}
                onChange={useCallback((e: string[]) => setState({ useMonth: e }), [setState])}
                className={css.select}
                placeholder="Use month"
                options={useMemo(() => {
                    const m = (use_month?.split(",") || []).sort((a, b) => getMonthNum(a) - getMonthNum(b))
                    return m.map((i) => {
                        return { value: i, label: i }
                    })
                }, [use_month])}
            />
            <div className={css.pointsSlider}>
                Points
                <div className={cn(compCss.noTooltip, compCss.sliderWrapper)}>
                    <Nouislider
                        instanceRef={(d) => (sliderRef.current = d)}
                        tooltips={tooltips}
                        connect
                        range={range}
                        onChange={onRangeChange}
                        start={startPoint.current}
                        behaviour={"tap-drag"}
                        step={5}
                    />
                    <div className={css.minValue} ref={minRef} />
                    <div className={css.maxValue} ref={maxRef} />
                </div>
            </div>
            <label htmlFor="withSold" className={css.toggleWrapper}>
                Hide Sold Listings
                <Toggle className={css.soldToggle} id="withSold" checked={!withSold} onChange={useCallback((e) => setState({ withSold: !e }), [setState])} />
            </label>
            <SortDropdown />
        </div>
    )
}

function SortDropdown() {
    const { sort, setState } = useDealsContext()
    const { isAuthenticated } = useUser()
    const menuProps = useMemo<MenuProps>(
        () => ({
            className: css.sortingMenu,
            selectedKeys: [sort],
            onClick: (i: { key: string }) => setState({ sort: i.key }),
            items: [
                {
                    key: "stars:desc",
                    text: (
                        <>
                            <strong>Deal score</strong> high to low
                        </>
                    )
                },
                {
                    key: "price_per_point:desc",
                    text: (
                        <>
                            <strong>Price per point</strong> high to low
                        </>
                    )
                },
                {
                    key: "price_per_point:asc",
                    text: (
                        <>
                            <strong>Price per point</strong> low to high
                        </>
                    )
                },
                {
                    key: "points_on_contract:desc",
                    text: (
                        <>
                            <strong>Points on contract</strong> high to low
                        </>
                    )
                },
                {
                    key: "points_on_contract:asc",
                    text: (
                        <>
                            <strong>Points on contract</strong> low to high
                        </>
                    )
                },
                {
                    key: "price:desc",
                    text: (
                        <>
                            <strong>Price</strong> high to low
                        </>
                    )
                },
                {
                    key: "price:asc",
                    text: (
                        <>
                            <strong>Price</strong> low to high
                        </>
                    )
                },
                {
                    key: "parsed_date:desc",
                    text: (
                        <>
                            <strong>Newest</strong> first
                        </>
                    )
                },
                {
                    key: "parsed_date:asc",
                    text: (
                        <>
                            <strong>Oldest</strong> first
                        </>
                    )
                },
                {
                    key: "listing_popularity.totalViews:desc",
                    text: (
                        <>
                            <strong>Popularity</strong> high to low
                        </>
                    )
                },
                {
                    key: "listing_popularity.totalViews:asc",
                    text: (
                        <>
                            <strong>Popularity</strong> low to high
                        </>
                    )
                }
            ].map((i) => {
                return {
                    label: i.text,
                    key: i.key
                }
            })
        }),
        [sort]
    )
    return (
        <Dropdown
            disabled={!isAuthenticated}
            menu={menuProps}
            /* placement={placement} getPopupContainer={() => containerRef.current} */
            trigger={["click"]}
            arrow
            overlayClassName={css.sortingMenuOverlay}>
            <button className={css.sortingBtn} aria-label="sort">
                Sort <Sort className={css.sortIcon} />
            </button>
        </Dropdown>
    )
}
